'use client';

import { HeartIcon } from "lucide-react";
import { useOptimistic, useTransition } from "react";
import { usePathname, useSearchParams } from "next/navigation";

import { toast } from '@/vibes/soul/primitives/toaster';
import { toggleWishlistItem } from "~/app/[locale]/(default)/_actions/wishlist-toggle";

interface WishlistButtonProps {
  productId: number;
  isInWishlist?: boolean;
  showWishlist?: boolean;
  className?: string;
  isLoggedIn?: boolean;
}

export function WishlistButton({
  productId,
  isInWishlist = false,
  showWishlist = false,
  className,
  isLoggedIn = true
}: WishlistButtonProps) {
  const [isPending, startTransition] = useTransition();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const redirectTo = `${pathname}?${searchParams.toString()}`;

  const [optimisticIsInWishlist, addOptimisticIsInWishlist] = useOptimistic(
    isInWishlist,
    (state, newIsInWishlist: boolean) => newIsInWishlist
  );

  const handleClick = () => {
    if (isPending) return;

    // If user is not logged in, show login message
    if (!isLoggedIn) {
      toast.info("Please log in to add items to your wishlist", {
        description: "Sign in to save your favorite products",
        action: {
          label: "Log In",
          onClick: () => {
            window.location.href = `/login?redirectTo=${encodeURIComponent(redirectTo)}`;
          }
        }
      });
      return;
    }

    startTransition(async () => {
      try {
        // Optimistic update
        addOptimisticIsInWishlist(!optimisticIsInWishlist);

        // Create form data
        const formData = new FormData();
        formData.append('productId', productId.toString());
        formData.append('redirectTo', redirectTo);

        // Submit the action
        await toggleWishlistItem(formData);
      } catch (error) {
        // Revert optimistic update on error
        addOptimisticIsInWishlist(optimisticIsInWishlist);
        console.error('Wishlist toggle error:', error);
      }
    });
  };

  if (!showWishlist) {
    return null;
  }

  return (
    <button
      className={`absolute top-3 right-3 cursor-pointer ${className || ''}`}
      type="button"
      aria-label={optimisticIsInWishlist ? "Remove from wishlist" : "Add to wishlist"}
      aria-pressed={optimisticIsInWishlist}
      onClick={handleClick}
      disabled={isPending}
    >
      <HeartIcon
        className={`${optimisticIsInWishlist
          ? 'text-[hsl(var(--primary))] fill-[hsl(var(--primary))]'
          : 'text-[hsl(var(--primary))] hover:fill-[hsl(var(--primary))]'
          } ${isPending ? 'opacity-50' : ''}`}
        aria-hidden="true"
      />
    </button>
  );
}